import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, BookOpen, Map, Compass } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import useTitle from "../lib/useTitle";

const heroImg = "https://images.unsplash.com/photo-1661622960824-86b510e68449?w=2000&q=80";

export default function Guides() {
  const { t, lang } = useI18n();
  useTitle(t.nav.guides);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/guides").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const grouped = items.reduce((acc, g) => {
    const cat = g.category || "Djerba";
    (acc[cat] = acc[cat] || []).push(g);
    return acc;
  }, {});

  return (
    <main data-testid="guides-page">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden bg-wostel-sage">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-wostel-navy/80 via-wostel-navy/20 to-transparent" />
        <div className="container-x relative z-10 pb-16 pt-32 text-sand-50">
          <span className="eyebrow text-wostel-sun">{t.nav.guides}</span>
          <h1 className="font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-5 max-w-3xl leading-[0.95]">{t.guides.title}</h1>
          <p className="mt-6 max-w-2xl text-lg text-sand-50/85 leading-relaxed">{t.guides.sub}</p>
        </div>
        {/* floating chips */}
        <div className="hidden md:flex absolute right-12 top-1/3 flex-col gap-3 z-10">
          <div className="bg-white text-wostel-ink px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-semibold rotate-3">
            <BookOpen size={14}/> {t.guides.chips.pdfs}
          </div>
          <div className="bg-wostel-sun text-wostel-ink px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-semibold -rotate-2">
            <Map size={14}/> {t.guides.chips.maps}
          </div>
          <div className="bg-wostel-cyan text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-semibold rotate-1">
            <Compass size={14}/> {t.guides.chips.offgrid}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="container-x py-20">
        {items.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-sand-200 mb-6">
              <BookOpen size={28} className="text-wostel-ink/50"/>
            </div>
            <p className="text-wostel-ink/60 max-w-md mx-auto" data-testid="guides-empty">{t.guides.empty}</p>
          </div>
        ) : (
          Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="mb-20" data-testid={`guides-cat-${cat}`}>
              <div className="flex items-end justify-between mb-10 flex-wrap gap-2">
                <h2 className="font-extrabold text-3xl sm:text-4xl tracking-tight">{cat}</h2>
                <span className="text-xs uppercase tracking-[0.25em] text-wostel-ink/50">{list.length} {list.length > 1 ? "guides" : "guide"}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {list.map((g, i) => {
                  const title = (lang === "fr" ? g.title_fr : g.title_en) || g.title_en;
                  const desc = (lang === "fr" ? g.description_fr : g.description_en) || "";
                  const cover = g.cover_image || heroImg;
                  return (
                    <motion.article
                      key={g.id}
                      data-testid={`guide-card-${g.id}`}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.06 }}
                      className="group bg-white rounded-3xl overflow-hidden border border-sand-300/60 hover:shadow-2xl transition-all duration-500"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img src={cover} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"/>
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-sand-50">
                          <h3 className="font-extrabold text-xl tracking-tight leading-tight">{title}</h3>
                        </div>
                      </div>
                      <div className="p-6">
                        {desc && <p className="text-sm text-wostel-ink/70 leading-relaxed mb-5 line-clamp-3">{desc}</p>}
                        <a href={g.pdf_url} target="_blank" rel="noreferrer" data-testid={`guide-download-${g.id}`}
                           className="inline-flex items-center gap-2 text-sm font-semibold text-wostel-cyan hover:gap-3 transition-all">
                          <Download size={16}/> {t.guides.download}
                        </a>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </section>
    </main>
  );
}
